import React, { useEffect, useState } from "react";
import { pickPortfolio } from "./selectedPortfolio";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Download, Loader2, AlertTriangle } from "lucide-react";
import { API, fmtScore, bandMeta } from "./shared";
import { SCHOOL_COLOR, PORTFOLIO_COLOR } from "./portfolioFlow";
import { PartialTag } from "./PartialTag";
import { GapTrendBadge, useLargestGapTrend } from "./GapTrendBadge";
import { downloadFile } from "@/lib/roleContext";
import { toast } from "sonner";
import { ComparePillars, takeaway } from "./ComparePillars";
import { bandOf } from "@/components/Dumbbell";

const GAP_ALERT = 10;

const Score = ({ label, color, value, testid, answered, total }) => {
  const m = value != null ? bandMeta(value) : null;
  return (
    <div className="flex-1 min-w-[140px] rounded-xl border border-slate-200 bg-white p-4" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-xs text-slate-500"><span className="h-2 w-2 rounded-full" style={{ background: color }} />{label}</div>
      <div className="mt-1 text-3xl font-bold text-slate-900">{value != null ? fmtScore(value) : "—"}<span className="text-sm font-normal text-slate-500"> / 100</span></div>
      <div className="mt-1 flex flex-wrap gap-1.5">
        {m && <span className="inline-block rounded-md px-2 py-0.5 text-xs font-semibold" style={{ background: m.bg, color: m.fg }}>{m.label}</span>}
        <PartialTag answered={answered} total={total} testid={`${testid}-partial`} />
      </div>
    </div>
  );
};

// Stage 4: one school's School vs Portfolio comparison (provisional or final).
export default function SchoolCompare() {
  const { periodId, tenantId } = useParams();
  const navigate = useNavigate();
  const [pid, setPid] = useState("");
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const trend = useLargestGapTrend(pid, { tenantId, periodId });
  const [dl, setDl] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const me = await (await fetch(`${API}/api/portfolio/me`, { credentials: "include" })).json();
        const p = pickPortfolio(me.portfolios)?.id; setPid(p);
        const r = await fetch(`${API}/api/portfolio/${p}/periods/${periodId}/schools/${tenantId}/compare`, { credentials: "include" });
        const j = await r.json();
        if (!r.ok) throw new Error(j.detail || "Couldn't load this comparison");
        setD(j);
      } catch (e) { setErr(e.message); }
    })();
  }, [periodId, tenantId]);

  if (err) return <div className="py-16 text-center text-sm text-red-600" data-testid="compare-error">{err}</div>;
  if (!d) return <div className="py-16 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-slate-400" /></div>;

  return (
    <div className="space-y-4" data-testid="school-compare">
      <button onClick={() => navigate(`/portfolio/schools/${tenantId}`)} className="inline-flex items-center gap-1.5 py-2 text-sm text-slate-500 hover:text-slate-800">
        <ArrowLeft className="h-4 w-4" /> {d.school_name}
      </button>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold text-slate-900 break-words">School vs Portfolio</h1>
          <p className="text-sm text-slate-500">{d.school_name} · {d.period?.name}
            {d.state === "provisional" && <span className="ml-2 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800" data-testid="compare-provisional-tag">Provisional</span>}
            {trend[tenantId] && <span className="ml-2"><GapTrendBadge trend={trend[tenantId]} testid="compare-gap-trend" /></span>}
          </p>
        </div>
        <span className="inline-flex flex-col items-end">
          {d.can_download ? (
            <button onClick={async () => {
                setDl(true);
                try { await downloadFile(`${API}/api/portfolio/${pid}/periods/${periodId}/schools/${tenantId}/compare.pdf`, "school-vs-portfolio.pdf"); }
                catch (e) { toast.error(e.message); } finally { setDl(false); }
              }} disabled={dl} data-testid="compare-download-pdf"
              className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-3 py-2 text-sm font-semibold text-white disabled:opacity-70">
              {dl ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download PDF</button>
          ) : (
            <button disabled data-testid="compare-download-pdf" className="inline-flex items-center gap-1.5 rounded-md bg-slate-200 px-3 py-2 text-sm font-semibold text-slate-600"><Download className="h-4 w-4" /> Download PDF</button>
          )}
          {!d.can_download && d.download_reason && <span className="mt-1 text-xs text-slate-500" data-testid="compare-download-reason">{d.download_reason}</span>}
        </span>
      </div>

      {d.state === "disabled" ? (
        <div className="rounded-xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-600" data-testid="compare-disabled-reason">{d.reason}</div>
      ) : (
        <>
          {d.banner && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="compare-provisional-banner">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {d.banner}
            </div>
          )}
          <div className="flex flex-wrap gap-3">
            <Score label="School" color={SCHOOL_COLOR} value={d.school_overall} testid="compare-school-overall" answered={d.school_answered} total={d.total_indicators} />
            <Score label="Portfolio" color={PORTFOLIO_COLOR} value={d.portfolio_overall} testid="compare-portfolio-overall" answered={d.portfolio_answered} total={d.total_indicators} />
            <div className="flex-1 min-w-[140px] rounded-xl border border-slate-200 bg-white p-4" data-testid="compare-overall-gap">
              <div className="text-xs text-slate-500">Gap (Portfolio − School)</div>
              <div className={`mt-1 text-3xl font-bold ${d.overall_gap != null && Math.abs(d.overall_gap) >= GAP_ALERT ? "text-red-700" : "text-slate-900"}`}>
                {d.overall_gap != null ? `${d.overall_gap > 0 ? "+" : ""}${d.overall_gap}` : "—"}
              </div>
              {bandOf(d.school_overall) && bandOf(d.portfolio_overall) && (
                <div className="mt-1 text-[13px] text-slate-600" data-testid="compare-overall-band-change">{bandOf(d.school_overall) === bandOf(d.portfolio_overall) ? `Both ${bandOf(d.school_overall)}` : `${bandOf(d.school_overall)} → ${bandOf(d.portfolio_overall)}`}</div>
              )}
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 border-l-4 border-l-[#6b3fa0] bg-white px-4 py-3 text-[15px] font-semibold text-[#111827]" data-testid="compare-takeaway">{takeaway(d.pillars)}</div>
          <ComparePillars pillars={d.pillars} provisional={d.state !== "ready"} />
        </>
      )}
      <p className="text-xs text-slate-500" data-testid="compare-private-note">This comparison is visible only to your Portfolio. The school never sees your assessment or this report.</p>
    </div>
  );
}
